import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Button, ScrollContainer, Text } from '@factorialco/deprecated-design-system'
import { CompetenciesFinderFactorialCompetencyFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  type SelectedCompetency,
  toSelectedCompetency,
} from 'modules/competencies/hooks/useCompetencyFinder'

import { SuggestionItem } from './SuggestionItem'
import { SuggestionSkeletons } from './SuggestionSkeleton'
import {
  type CompetencySuggestionTarget,
  canSuggestForTarget,
  getTargetIdentifier,
  useFactorialCompetenciesSuggestions,
} from './useSuggestions'

const INITIAL_SUGGESTIONS = 6
const MAX_SUGGESTIONS = 12

type Props = {
  target: CompetencySuggestionTarget
  initialUuids: string[]
  excludeUuids: string[]
  onAdd: (competency: SelectedCompetency) => void
}

const SuggestionsContent = ({ target, initialUuids, excludeUuids, onAdd }: Props) => {
  const [showCount, setShowCount] = useState(INITIAL_SUGGESTIONS)
  const { suggestions, loading, error } = useFactorialCompetenciesSuggestions({
    target,
    initialUuids: initialUuids,
  })

  const visibleSuggestions = useMemo(() => {
    return suggestions.slice(0, showCount)
  }, [suggestions, showCount])

  const visibleCompetencies = useMemo(() => {
    const excludeSet = new Set(excludeUuids)
    return visibleSuggestions.filter(({ uuid }) => !excludeSet.has(uuid))
  }, [visibleSuggestions, excludeUuids])

  const canLoadMore = showCount < MAX_SUGGESTIONS && suggestions.length > showCount
  const canAutoAdvance =
    !loading &&
    suggestions.length > 0 &&
    visibleCompetencies.length === 0 &&
    canLoadMore &&
    showCount === INITIAL_SUGGESTIONS

  const handleAddCompetency = (competency: CompetenciesFinderFactorialCompetencyFragment) => {
    onAdd(toSelectedCompetency(competency, 'suggestions'))
  }

  const handleLoadMore = useCallback(() => {
    setShowCount((prev) => prev + INITIAL_SUGGESTIONS)
  }, [])

  useEffect(() => {
    if (canAutoAdvance) {
      handleLoadMore()
    }
  }, [canAutoAdvance, handleLoadMore])

  if (error || (!loading && visibleCompetencies.length === 0 && !canLoadMore)) {
    return null
  }

  const title = i18n.t('competencies_v3.competencies_finder.suggestions_title')
  return (
    <Box role="region" aria-label={title} gap="s8" flexDirection="column">
      <Text.Normal size="200" weight="medium" color="grey1000">
        {title}
      </Text.Normal>
      <ScrollContainer maxHeight="s400">
        <Box gap="s8" flexDirection="column">
          <Box flexDirection="column" gap="s8" role="list" aria-label={title}>
            {loading && (
              <SuggestionSkeletons
                quantity={INITIAL_SUGGESTIONS}
                identifier="initial-suggestions"
              />
            )}
            {!loading &&
              visibleCompetencies.map((competency) => (
                <SuggestionItem
                  key={`suggestion-${competency.uuid}`}
                  competency={competency}
                  onAdd={() => handleAddCompetency(competency)}
                />
              ))}
          </Box>

          {!loading && canLoadMore && (
            <Button onClick={handleLoadMore} type="secondary">
              {i18n.t('competencies_v3.competencies_finder.load_more')}
            </Button>
          )}
        </Box>
      </ScrollContainer>
    </Box>
  )
}

export const Suggestions = ({ target, initialUuids, excludeUuids, onAdd }: Props) => {
  if (!canSuggestForTarget(target)) {
    return null
  }

  const identifier = getTargetIdentifier(target)

  return (
    <SuggestionsContent
      key={identifier}
      target={target}
      initialUuids={initialUuids}
      excludeUuids={excludeUuids}
      onAdd={onAdd}
    />
  )
}
